/**
 * Block 13 PR-G Phase 4A — IDB autosave layer для contract drafts.
 *
 * Mirror Block 12 `quoteDraftIdbSync.ts` (PR-D-A Phase D5 Step 9). Persistent
 * mirror Zustand store'а (`useContractDraftStore`). Хранит черновики keyed by
 * `draftKey` = contractId UUID. Переживает F5 refresh, закрытие/открытие
 * браузера, cross-tab (last-writer-wins).
 *
 * **Отдельная DB `contract-drafts`** (НЕ пересекается с `pomoshnik-offline-v1`
 * (контракты как explicit saves через savedContractsIdb)):
 *   1. Schema isolation — добавление/удаление contract drafts store не требует
 *      version bump main DB.
 *   2. Lifecycle isolation — `clearAllContractDraftsFromIdb()` (logout) не трогает
 *      saved contracts (`pomoshnik-offline-v1.contracts` store).
 *   3. Mirror Block 12 quote-drafts pattern для consistency.
 *
 * **Graceful degradation:** if `indexedDB === undefined` (SSR / private
 * browsing / quota exhausted) — функции возвращают `undefined` для load,
 * no-op для save/clear. НЕ throw — UI продолжает работу без durability.
 *
 * Reference:
 *   - `features/calculator/quote/state/quoteDraftIdbSync.ts`
 *   - ADR-0025 §Q3 (separate stores rationale)
 */

import { openDB, type IDBPDatabase, type DBSchema } from 'idb';

import type { ContractFormState } from './contractReducer';

export const CONTRACT_DRAFTS_DB_NAME = 'contract-drafts';
export const CONTRACT_DRAFTS_DB_VERSION = 1;
export const CONTRACT_DRAFTS_STORE = 'drafts';

export interface ContractDraftsDBSchema extends DBSchema {
  drafts: {
    /** draftKey = contractId UUID. */
    key: string;
    value: ContractFormState;
  };
}

let dbPromise: Promise<IDBPDatabase<ContractDraftsDBSchema>> | null = null;

function isIdbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function logWarn(scope: string, err: unknown): void {
  if (import.meta.env.DEV) {
    // P-009: НЕ log raw err (DOMException может содержать PII record data).
    // Только err.name (e.g., 'QuotaExceededError', 'InvalidStateError').
    const errName = err instanceof Error ? err.name : 'unknown';
    console.warn(`[contractDraftIdbSync] ${scope} failed`, errName);
  }
}

async function getDb(): Promise<IDBPDatabase<ContractDraftsDBSchema> | null> {
  if (!isIdbAvailable()) return null;
  if (!dbPromise) {
    dbPromise = openDB<ContractDraftsDBSchema>(
      CONTRACT_DRAFTS_DB_NAME,
      CONTRACT_DRAFTS_DB_VERSION,
      {
        upgrade(db) {
          if (!db.objectStoreNames.contains(CONTRACT_DRAFTS_STORE)) {
            db.createObjectStore(CONTRACT_DRAFTS_STORE);
          }
        },
        terminated() {
          dbPromise = null;
        },
        blocking() {
          dbPromise?.then((db) => db.close()).catch(() => {});
          dbPromise = null;
        },
      },
    );
  }
  try {
    return await dbPromise;
  } catch (err) {
    logWarn('openDB', err);
    dbPromise = null;
    return null;
  }
}

export async function saveContractDraftToIdb(key: string, state: ContractFormState): Promise<void> {
  const db = await getDb();
  if (!db) return;
  try {
    await db.put(CONTRACT_DRAFTS_STORE, state, key);
  } catch (err) {
    logWarn('saveContractDraftToIdb', err);
  }
}

export async function loadContractDraftFromIdb(
  key: string,
): Promise<ContractFormState | undefined> {
  const db = await getDb();
  if (!db) return undefined;
  try {
    return await db.get(CONTRACT_DRAFTS_STORE, key);
  } catch (err) {
    logWarn('loadContractDraftFromIdb', err);
    return undefined;
  }
}

export async function clearContractDraftFromIdb(key: string): Promise<void> {
  const db = await getDb();
  if (!db) return;
  try {
    await db.delete(CONTRACT_DRAFTS_STORE, key);
  } catch (err) {
    logWarn('clearContractDraftFromIdb', err);
  }
}

export async function clearAllContractDraftsFromIdb(): Promise<void> {
  const db = await getDb();
  if (!db) return;
  try {
    await db.clear(CONTRACT_DRAFTS_STORE);
  } catch (err) {
    logWarn('clearAllContractDraftsFromIdb', err);
  }
}

/** Для тестов: сбрасывает singleton connection. */
export function _resetContractDraftIdbCacheForTests(): void {
  dbPromise = null;
}
